import { NotificationProcessCatalogueEntry } from 'framework/api/methods'

import { NotificationRecipientCard } from './EmailAlertsCardList/EmailAlertReportCard'

/*
 * BDL20-4262 — pure helpers around the notification process catalogue.
 * The senders resolve recipients by EXACT ServiceName, so everything that offers or matches a
 * ServiceName on the Email Alerts page goes through the catalogue rather than free text.
 */

export const OUTCOME_SUCCESS = 'Success'
export const OUTCOME_FAILURE = 'Failure'
export type CatalogueOutcome = 'Success' | 'Failure'

/** Outcome filter on the page: '' = all, 'Other' = reports and anything without an outcome. */
export type OutcomeFilter = '' | CatalogueOutcome | 'Other'

export interface CataloguePartner {
  id: string
  name: string
  code: string
}

/** One distinct process (one per `process` value) as offered in the New Email Group pick-list. */
export interface CatalogueProcess {
  process: string
  displayName: string
  defaultTag: string
  hasOutcomes: boolean
  partnerCodes: string[]
}

/** What a "Set up" button hands to the New Email Group drawer. */
export interface EmailAlertGroupPrefill {
  partnerId: string
  partnerName: string
  partnerCode: string
  process: string
  outcome: CatalogueOutcome | null
}

const upper = (value: string | undefined | null): string =>
  (value ?? '').trim().toUpperCase()

export const cardKey = (partnerId: string, serviceName: string): string =>
  `${partnerId}|${upper(serviceName)}`

export const catalogueEntryAppliesToPartner = (
  entry: NotificationProcessCatalogueEntry,
  partnerCode: string,
): boolean =>
  entry.partnerCodes.length === 0 ||
  entry.partnerCodes.some((code) => upper(code) === upper(partnerCode))

export const findCatalogueEntry = (
  entries: NotificationProcessCatalogueEntry[],
  serviceName: string,
): NotificationProcessCatalogueEntry | undefined =>
  entries.find((entry) => upper(entry.serviceName) === upper(serviceName))

/** Exact ServiceName the senders resolve for a process + outcome (mirrors NotificationServiceNames.Success/Failure). */
export const serviceNameForOutcome = (
  process: string,
  outcome: CatalogueOutcome | null,
): string => (outcome ? `${process}${outcome}` : process)

export const describeOutcome = (
  displayName: string,
  outcome: CatalogueOutcome | null,
): string => (outcome ? `${displayName} - ${outcome}` : displayName)

/** Distinct processes, optionally narrowed to those the code sends for one partner. Catalogue order is kept. */
export const buildCatalogueProcesses = (
  entries: NotificationProcessCatalogueEntry[],
  partnerCode?: string,
): CatalogueProcess[] => {
  const byProcess = new Map<string, CatalogueProcess>()
  entries.forEach((entry) => {
    if (
      partnerCode !== undefined &&
      !catalogueEntryAppliesToPartner(entry, partnerCode)
    )
      return
    const existing = byProcess.get(entry.process)
    if (existing) {
      existing.hasOutcomes = existing.hasOutcomes || entry.outcome !== null
      return
    }
    byProcess.set(entry.process, {
      process: entry.process,
      displayName: entry.displayName,
      defaultTag: entry.defaultTag,
      hasOutcomes: entry.outcome !== null,
      partnerCodes: entry.partnerCodes,
    })
  })
  return Array.from(byProcess.values())
}

/** Outcome of a card: the catalogue first, otherwise the Success/Failure suffix convention the senders use. */
export const outcomeOfServiceName = (
  serviceName: string,
  entries: NotificationProcessCatalogueEntry[],
): CatalogueOutcome | null => {
  const entry = findCatalogueEntry(entries, serviceName)
  if (entry) return entry.outcome
  const name = upper(serviceName)
  if (name.endsWith(upper(OUTCOME_SUCCESS))) return OUTCOME_SUCCESS
  if (name.endsWith(upper(OUTCOME_FAILURE))) return OUTCOME_FAILURE
  return null
}

/* ---------- Process filter options ---------- */

export const ALL_PROCESSES = ''
/** Prefix for a legacy filter value: a ServiceNameDescription not represented in the catalogue. */
export const PROCESS_FILTER_DESCRIPTION_PREFIX = 'description:'

export interface ProcessFilterOption {
  value: string
  label: string
}

/** Catalogue processes first (value = process), then any recorded descriptions the catalogue does not cover. */
export const buildProcessFilterOptions = (
  entries: NotificationProcessCatalogueEntry[],
  descriptions: string[],
): ProcessFilterOption[] => {
  const options: ProcessFilterOption[] = buildCatalogueProcesses(entries).map(
    (process) => ({ value: process.process, label: process.displayName }),
  )
  const covered = new Set<string>()
  entries.forEach((entry) => {
    covered.add(upper(entry.displayName))
    covered.add(upper(describeOutcome(entry.displayName, entry.outcome)))
  })
  const seen = new Set<string>()
  descriptions.forEach((description) => {
    const key = upper(description)
    if (!key || covered.has(key) || seen.has(key)) return
    seen.add(key)
    options.push({
      value: `${PROCESS_FILTER_DESCRIPTION_PREFIX}${description}`,
      label: description,
    })
  })
  return options
}

/* ---------- Card list rows ---------- */

export interface SingleCardRow {
  kind: 'single'
  key: string
  process: string
  card: NotificationRecipientCard
  outcome: CatalogueOutcome | null
}

export interface PairedCardRow {
  kind: 'paired'
  key: string
  process: string
  partner: CataloguePartner
  displayName: string
  tag: string
  success: NotificationRecipientCard | null
  failure: NotificationRecipientCard | null
}

/** A (partner, ServiceName) the code sends for which nobody receives — a silent alert. */
export interface NotConfiguredRow {
  kind: 'missing'
  key: string
  process: string
  partner: CataloguePartner
  entry: NotificationProcessCatalogueEntry
}

export type CardListRow = SingleCardRow | PairedCardRow | NotConfiguredRow

export interface BuildCardListRowsInput {
  /** Every card for the current partner filter — unpaged and unsearched, so pairs are never split. */
  cards: NotificationRecipientCard[]
  /** Catalogue entries; empty when the catalogue request failed (no pairing, no placeholders). */
  entries: NotificationProcessCatalogueEntry[]
  /** Active partners, used to resolve catalogue partner codes to an id/name. */
  partners: CataloguePartner[]
  /** null = all partners. */
  selectedPartner: CataloguePartner | null
  outcomeFilter: OutcomeFilter
  processFilter: string
  searchTerm: string
  /** Keys (cardKey) the server search matched — covers recipient emails, which cards do not carry. */
  searchMatchedKeys: Set<string> | null
}

const partnerOfCard = (card: NotificationRecipientCard): CataloguePartner => ({
  id: card.partnerId,
  name: card.partnerName ?? '',
  code: card.partnerCode ?? '',
})

const outcomeMatches = (
  outcome: CatalogueOutcome | null,
  filter: OutcomeFilter,
): boolean => {
  if (filter === '') return true
  if (filter === 'Other') return outcome === null
  return outcome === filter
}

const textMatches = (term: string, ...values: (string | undefined)[]) =>
  values.some((value) => (value ?? '').toLowerCase().includes(term))

const cardsOfRow = (row: CardListRow): NotificationRecipientCard[] => {
  if (row.kind === 'single') return [row.card]
  if (row.kind === 'paired')
    return [row.success, row.failure].filter(
      (card): card is NotificationRecipientCard => card !== null,
    )
  return []
}

const rowMatchesProcessFilter = (
  row: CardListRow,
  processFilter: string,
): boolean => {
  if (processFilter === ALL_PROCESSES) return true
  if (processFilter.startsWith(PROCESS_FILTER_DESCRIPTION_PREFIX)) {
    const wanted = upper(
      processFilter.slice(PROCESS_FILTER_DESCRIPTION_PREFIX.length),
    )
    return cardsOfRow(row).some(
      (card) => upper(card.serviceNameDescription) === wanted,
    )
  }
  return row.process === processFilter
}

const rowMatchesSearch = (
  row: CardListRow,
  term: string,
  matchedKeys: Set<string> | null,
): boolean => {
  if (!term) return true
  if (row.kind === 'missing') {
    return textMatches(
      term,
      row.partner.name,
      row.partner.code,
      row.entry.serviceName,
      row.entry.displayName,
    )
  }
  const cards = cardsOfRow(row)
  if (
    cards.some(
      (card) =>
        textMatches(
          term,
          card.partnerName,
          card.partnerCode,
          card.serviceName,
          card.serviceNameDescription,
        ) ||
        (matchedKeys?.has(cardKey(card.partnerId, card.serviceName)) ?? false),
    )
  )
    return true
  if (row.kind === 'paired')
    return textMatches(
      term,
      row.partner.name,
      row.partner.code,
      row.displayName,
    )
  return false
}

/**
 * Turns the unpaged card list into display rows: Success/Failure of one process paired into one
 * row, every other card on its own, then placeholders for each (partner, ServiceName) the code
 * sends but nobody receives. Process, outcome and search filters are applied to the rows.
 */
export const buildCardListRows = ({
  cards,
  entries,
  partners,
  selectedPartner,
  outcomeFilter,
  processFilter,
  searchTerm,
  searchMatchedKeys,
}: BuildCardListRowsInput): CardListRow[] => {
  const pairOutcomes = outcomeFilter === ''
  const cardByKey = new Map<string, NotificationRecipientCard>()
  cards.forEach((card) =>
    cardByKey.set(cardKey(card.partnerId, card.serviceName), card),
  )

  const realRows: CardListRow[] = []
  const pairedByKey = new Map<string, PairedCardRow>()

  cards.forEach((card) => {
    const entry = findCatalogueEntry(entries, card.serviceName)
    if (entry && entry.outcome && pairOutcomes) {
      const key = `${card.partnerId}|${entry.process}`
      let row = pairedByKey.get(key)
      if (!row) {
        row = {
          kind: 'paired',
          key,
          process: entry.process,
          partner: partnerOfCard(card),
          displayName: entry.displayName,
          tag: card.tag,
          success: null,
          failure: null,
        }
        pairedByKey.set(key, row)
        realRows.push(row)
      }
      if (entry.outcome === OUTCOME_SUCCESS) row.success = card
      else row.failure = card
      return
    }
    realRows.push({
      kind: 'single',
      key: cardKey(card.partnerId, card.serviceName),
      process: entry ? entry.process : card.serviceName,
      card,
      outcome: outcomeOfServiceName(card.serviceName, entries),
    })
  })

  // Placeholders: with one partner selected, entries with no partner codes apply too; across all
  // partners only explicit codes are expanded, otherwise every partner would get every process.
  const placeholderRows: CardListRow[] = []
  const partnerUniverse: CataloguePartner[] = selectedPartner
    ? [selectedPartner]
    : partners.filter((partner) =>
        entries.some(
          (entry) =>
            entry.partnerCodes.length > 0 &&
            catalogueEntryAppliesToPartner(entry, partner.code),
        ),
      )

  partnerUniverse.forEach((partner) => {
    entries.forEach((entry) => {
      if (!partner.code && entry.partnerCodes.length > 0) return
      if (!catalogueEntryAppliesToPartner(entry, partner.code)) return
      if (!selectedPartner && entry.partnerCodes.length === 0) return
      if (cardByKey.has(cardKey(partner.id, entry.serviceName))) return

      if (entry.outcome && pairOutcomes) {
        const key = `${partner.id}|${entry.process}`
        if (pairedByKey.has(key)) return // the other side exists; it already shows this side as missing
        const row: PairedCardRow = {
          kind: 'paired',
          key,
          process: entry.process,
          partner,
          displayName: entry.displayName,
          tag: entry.defaultTag,
          success: null,
          failure: null,
        }
        pairedByKey.set(key, row)
        placeholderRows.push(row)
        return
      }
      placeholderRows.push({
        kind: 'missing',
        key: cardKey(partner.id, entry.serviceName),
        process: entry.process,
        partner,
        entry,
      })
    })
  })

  placeholderRows.sort((a, b) => {
    const partnerA = a.kind === 'single' ? '' : a.partner.name
    const partnerB = b.kind === 'single' ? '' : b.partner.name
    if (partnerA !== partnerB) return partnerA.localeCompare(partnerB)
    const nameA =
      a.kind === 'missing'
        ? a.entry.displayName
        : a.kind === 'paired'
        ? a.displayName
        : ''
    const nameB =
      b.kind === 'missing'
        ? b.entry.displayName
        : b.kind === 'paired'
        ? b.displayName
        : ''
    return nameA.localeCompare(nameB)
  })

  const term = searchTerm.trim().toLowerCase()
  return [...realRows, ...placeholderRows].filter((row) => {
    if (row.kind === 'single' && !outcomeMatches(row.outcome, outcomeFilter))
      return false
    if (
      row.kind === 'missing' &&
      !outcomeMatches(row.entry.outcome, outcomeFilter)
    )
      return false
    if (!rowMatchesProcessFilter(row, processFilter)) return false
    return rowMatchesSearch(row, term, searchMatchedKeys)
  })
}

/** Sum of configured recipients across the cards in the given rows. */
export const countRecipients = (rows: CardListRow[]): number =>
  rows.reduce(
    (sum, row) =>
      sum +
      cardsOfRow(row).reduce((inner, card) => inner + card.recipientCount, 0),
    0,
  )

/** Count of (partner, ServiceName) pairs the code sends for which nobody receives. */
export const countNotConfigured = (rows: CardListRow[]): number =>
  rows.reduce((sum, row) => {
    if (row.kind === 'missing') return sum + 1
    if (row.kind === 'paired')
      return sum + (row.success ? 0 : 1) + (row.failure ? 0 : 1)
    return sum
  }, 0)

export const isPlaceholderRow = (row: CardListRow): boolean =>
  row.kind === 'missing' ||
  (row.kind === 'paired' && row.success === null && row.failure === null)
